import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setDrawing, setContext, setSize, setInitialized, setRenderError, selectDrawing, selectContext, selectSize, selectInitialized } from '../store/slices/wallManagerSlice';
import { selectJoint, selectScale, selectWallLayout } from '../store/slices/configurationSlice';
import { selectBricksLoading } from '../store/slices/bricksSlice';
import { JOINT_TEXTURE_SIZE_MM } from '../constants';
import { getCachedImage, loadImage } from '../utils/imageCache';
import { rasterizeTile, toCanvas } from '../wall/rasterize';
import { computeReliefMaps } from '../wall/relief';
import { drawWall, WallTileImage } from '../wall/renderer';

// Long enough to skip intermediate slider positions, short enough to feel live.
const RENDER_DEBOUNCE_MS = 120;

export const useWallManager = () => {
  const dispatch = useAppDispatch();
  const drawing = useAppSelector(selectDrawing);
  const ctx = useAppSelector(selectContext);
  const size = useAppSelector(selectSize);
  const initialized = useAppSelector(selectInitialized);
  const layout = useAppSelector(selectWallLayout);
  const joint = useAppSelector(selectJoint);
  const scale = useAppSelector(selectScale);
  const bricksLoading = useAppSelector(selectBricksLoading);
  const [tile, setTile] = useState<WallTileImage | null>(null);

  const setCanvasContext = useCallback((context: CanvasRenderingContext2D | null) => {
    dispatch(setContext(context));
  }, [dispatch]);

  const setCanvasSize = useCallback((height: number, width: number) => {
    dispatch(setSize({ height, width }));
  }, [dispatch]);

  const startDrawing = useCallback(() => {
    if (!initialized) {
      dispatch(setInitialized(true));
    }
  }, [initialized, dispatch]);

  const ready =
    initialized &&
    !bricksLoading &&
    layout.bricks.length > 0 &&
    layout.bricks.every((b) => getCachedImage(b.brick.displayImage));

  useEffect(() => {
    if (layout.bricks.length === 0) {
      setTile(null);
    }
    if (!ready) {
      return;
    }
    let cancelled = false;
    dispatch(setDrawing(true));
    const timer = setTimeout(async () => {
      try {
        const jointImage = await loadImage(joint.img);
        if (cancelled) return;
        const raster = rasterizeTile(layout, {
          pxPerMm: scale,
          jointImage,
          jointTextureSizeMm: JOINT_TEXTURE_SIZE_MM,
          brickImage: (brick) => getCachedImage(brick.displayImage)!,
        });
        const { diffuse } = computeReliefMaps(raster);
        if (!cancelled) {
          setTile({ image: toCanvas(raster.width, raster.height, diffuse), pxPerMm: raster.pxPerMm });
          dispatch(setRenderError(null));
        }
      } catch (e) {
        if (!cancelled) {
          dispatch(setRenderError(e instanceof Error ? e.message : String(e)));
        }
      } finally {
        if (!cancelled) dispatch(setDrawing(false));
      }
    }, RENDER_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      dispatch(setDrawing(false));
    };
  }, [ready, layout, joint, scale, dispatch]);

  useEffect(() => {
    if (!ctx) return;
    if (tile) {
      drawWall(ctx, tile, scale, size.width, size.height);
    } else {
      ctx.clearRect(0, 0, size.width, size.height);
    }
  }, [ctx, tile, scale, size.width, size.height]);

  return {
    drawing,
    setContext: setCanvasContext,
    setSize: setCanvasSize,
    startDrawing,
  };
};
